/**
 * The visual body of SessionStartModal: selectors, action buttons and the
 * lesson-entry shell around them.
 *
 * Split out from the modal itself only for size — these have no state of their
 * own and read everything from SharedProps.
 */

import { useId } from "react";
import SelectionButton from "../SelectionButton";
import PrimaryButton from "../PrimaryButton";
import SecondaryButton from "../SecondaryButton";
import VoicePreview, { VisualMode } from "../voice/VoicePreview";
import { Loader2, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  AUDIO_VOICE_MAP,
  toVoicePreviewOption,
  getLocalizedName,
} from "@/constants/audioVoice";
import { CHARACTER_MAP } from "@/constants/character";
import { CHARACTER_ICONS } from "@/assets/icons/characters";
import SpeakingPersonIcon from "@/components/icons/SpeakingPersonIcon";
import EditPencilIcon from "@/components/icons/EditPencilIcon";
import type {
  SessionStartAction,
  SharedProps,
} from "./sessionStartModalContract";
import "./lesson-entry.css";

// ─── Reusable pieces ─────────────────────────────────────────────────────────

function SelectorsBlock({
  formData,
  submitting,
  voicePreviewRef,
  handleProgress,
  handlePlayStateChange,
  openChangeLanguage,
  openChangeCharacter,
  openChangeVoice,
  isVoiceTrained,
  handleSpeakName,
}: Pick<
  SharedProps,
  | "formData"
  | "submitting"
  | "voicePreviewRef"
  | "handleProgress"
  | "handlePlayStateChange"
  | "openChangeLanguage"
  | "openChangeCharacter"
  | "openChangeVoice"
  | "isVoiceTrained"
  | "handleSpeakName"
>) {
  const contentCn =
    "modal-portrait:!py-2 modal-portrait-short:!py-1 modal-short-viewport:!py-2";
  const character = CHARACTER_MAP[formData.character?.characterId];
  const audioVoice = formData.voice
    ? AUDIO_VOICE_MAP[formData.voice.voiceId]
    : null;
  const voicePreviewOption = audioVoice
    ? toVoicePreviewOption(audioVoice, formData.language?.value)
    : null;

  const VoiceAvatarIcon = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openChangeCharacter();
      }}
      disabled={submitting}
      className="relative flex-shrink-0 w-[44px] h-[44px] rounded-full disabled:opacity-50 disabled:pointer-events-none"
      aria-label="Choose character"
    >
      {/* Avatar — use character avatar as proxy for voice face */}
      {CHARACTER_ICONS[character?.id] ? (
        <img
          src={CHARACTER_ICONS[character.id]}
          alt=""
          className="w-full h-full rounded-full object-cover"
        />
      ) : (
        <div className="w-full h-full rounded-full bg-[#E9E1F5] flex items-center justify-center font-bold text-[#5B3E8A] text-sm">
          {character?.name?.[0] || "?"}
        </div>
      )}
      {/* Persistent edit-pencil overlay */}
      <span className="absolute bottom-0 right-0 w-[18px] h-[18px] rounded-full bg-[#28543D] text-white flex items-center justify-center shadow-sm pointer-events-none">
        <EditPencilIcon />
      </span>
    </button>
  );

  return (
    <div className="et-entry-selectors flex w-full flex-col items-center justify-start space-y-1 text-center text-[#252525]">
      <p className="py-2 modal-portrait:py-1 modal-portrait-short:py-0.5 font-mulish text-[15px] modal-portrait-short:text-[13px] font-semibold text-[#252525]">
        Language of instruction
      </p>
      <SelectionButton
        icon={formData.language?.icon || ""}
        label={formData.language?.label || "Language"}
        buttonLabel="Change"
        onChange={openChangeLanguage}
        disabled={submitting}
        className="et-entry-selection w-full max-w-[450px]"
        contentClassName={contentCn}
      />
      <p className="py-2 modal-portrait:py-1 modal-portrait-short:py-0.5 font-mulish text-[15px] modal-portrait-short:text-[13px] font-semibold text-[#252525]">
        Voice &amp; speed
      </p>
      <SelectionButton
        icon={VoiceAvatarIcon}
        label={
          <div className="flex flex-col justify-start items-start gap-0.5 modal-portrait-short:gap-0">
            <div className="flex items-center gap-1.5">
              <span className="font-bold modal-portrait-short:text-sm whitespace-nowrap">
                {audioVoice
                  ? getLocalizedName(
                      audioVoice,
                      formData.language?.value ?? "en",
                    )
                  : "Voice"}
              </span>
              {isVoiceTrained && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSpeakName();
                  }}
                  aria-label="Hear your name"
                  className="inline-flex items-center justify-center w-[26px] h-[26px] flex-shrink-0 rounded-full bg-[#EEF0E3] text-[#28543D] border-none cursor-pointer p-0 transition-colors hover:bg-[#E2E9DC]"
                >
                  <SpeakingPersonIcon />
                </button>
              )}
            </div>
            <span className="text-xs italic text-[#7A8891] modal-portrait-short:truncate modal-portrait-short:max-w-[160px]">
              Speed :{" "}
              <span className="capitalize">
                {formData.voice?.speed || "medium"}
              </span>
            </span>
          </div>
        }
        buttonLabel="Change"
        onChange={openChangeVoice}
        disabled={submitting}
        className="et-entry-selection w-full max-w-[450px]"
        contentClassName={contentCn}
      />
      {/* Hidden voice preview for audio playback */}
      {voicePreviewOption && (
        <div className="hidden">
          <VoicePreview
            ref={voicePreviewRef}
            voice={voicePreviewOption}
            visualMode={VisualMode.COMPACT}
            onProgress={handleProgress}
            onPlayStateChange={handlePlayStateChange}
            defaultSpeed={formData.voice?.speed}
          />
        </div>
      )}
    </div>
  );
}

function ActionsBlock({
  formData,
  actions,
  submitting,
  pendingActionId,
  runAction,
  retakeItems,
  retakeDropdownOpen,
  setRetakeDropdownOpen,
  runRetake,
  buttonHeightClass,
  buttonTextClass,
}: Pick<
  SharedProps,
  | "formData"
  | "actions"
  | "submitting"
  | "pendingActionId"
  | "runAction"
  | "retakeItems"
  | "retakeDropdownOpen"
  | "setRetakeDropdownOpen"
  | "runRetake"
> & { buttonHeightClass: string; buttonTextClass: string }) {
  const buttonActions = actions.filter((action) => action.kind !== "link");
  const linkActions = actions.filter((action) => action.kind === "link");
  // Sits under the row rather than on the button: the reason names the way
  // out, and the way out is one of the other actions.
  const disabledReason =
    actions.find((action) => action.disabledReason)?.disabledReason ?? null;
  // et-ui's button carries `w-full md:w-fit`, so a plain `w-full` loses to it
  // on desktop. A lone button overrides that to keep the 280px pill the
  // single-action screens have always had; a pair keeps `w-full` and lets the
  // row size them, which is what the resume screen already shipped.
  const soleButtonWidthCn =
    buttonActions.length === 1 ? "!w-full !max-w-[280px] mx-auto" : "w-full";

  const renderLabel = (action: SessionStartAction) => {
    const isPending = pendingActionId === action.id;
    return (
      <span className="inline-flex items-center justify-center gap-2">
        {isPending && action.pendingLabel && (
          <Loader2 className="h-4 w-4 animate-spin" />
        )}
        {isPending && action.pendingLabel ? action.pendingLabel : action.label}
      </span>
    );
  };

  return (
    <div className="et-entry-actions flex flex-col justify-start items-center gap-5 w-full mt-auto">
      {buttonActions.length > 0 && (
        <div className="flex w-full max-w-[450px] flex-col gap-3 sm:flex-row sm:items-center modal-landscape:flex-col modal-landscape:sm:flex-col">
          {buttonActions.map((action) => {
            const Button =
              action.kind === "secondary" ? SecondaryButton : PrimaryButton;
            return (
              <Button
                key={action.id}
                onClick={() => runAction(action)}
                disabled={submitting || Boolean(action.disabledReason)}
                className={`${soleButtonWidthCn} !rounded-[6px] ${buttonHeightClass} ${buttonTextClass} ${
                  action.disabledReason ? "grayscale" : ""
                }`}
              >
                {renderLabel(action)}
              </Button>
            );
          })}
        </div>
      )}
      {disabledReason && (
        <p
          role="status"
          className="max-w-[450px] text-center font-mulish text-sm font-semibold italic text-[#B45309] modal-portrait:text-xs"
        >
          {disabledReason}
        </p>
      )}
      {linkActions.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={() => runAction(action)}
          disabled={submitting || Boolean(action.disabledReason)}
          className={`font-mulish font-bold leading-none text-[#28543D] hover:text-[#204833] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 my-2 ${buttonTextClass}`}
        >
          {pendingActionId === action.id && action.pendingLabel
            ? action.pendingLabel
            : action.label}
        </button>
      ))}
      {retakeItems.length > 0 && runRetake && (
        <div className="flex flex-col items-center gap-[5px]">
          {retakeItems.length === 1 ? (
            // Routed through runRetake, not called directly: the container's
            // handler closes this modal itself, but only after two awaits, and
            // an unlocked "Start Learning" in that gap boots a session the
            // survey then mounts on top of.
            <button
              type="button"
              onClick={() => runRetake(retakeItems[0].type)}
              disabled={submitting}
              className={`font-mulish font-bold leading-none text-[#28543D] hover:text-[#204833] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 my-2 ${buttonTextClass}`}
            >
              Retake {retakeItems[0].label}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setRetakeDropdownOpen((v) => !v)}
                disabled={submitting}
                className={`flex items-center gap-1 font-mulish font-bold leading-none text-[#28543D] hover:text-[#204833] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${buttonTextClass}`}
              >
                Retake assessments
                {retakeDropdownOpen ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: retakeDropdownOpen
                    ? `${retakeItems.length * 40 + 20}px`
                    : "0px",
                  opacity: retakeDropdownOpen ? 1 : 0,
                }}
              >
                <div className="flex flex-col divide-y divide-[#DCDCDC] border border-[#DCDCDC] rounded-[10px] p-[10px] gap-[10px]">
                  {retakeItems.map(({ type, label }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => runRetake(type)}
                      disabled={submitting}
                      className="w-full text-left pt-[10px] first:pt-0 font-mulish text-[15px] font-semibold leading-none text-[#28543D] hover:text-[#204833] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {formData.language.multilingual && (
        <p className="max-w-[540px] text-center text-[#252525] font-semibold italic font-mulish text-sm modal-portrait:text-xs pt-3 pb-1 modal-portrait:pt-1 modal-portrait:pb-0">
          For mixed languages, in noisy environments, speech recognition may be
          less accurate.
        </p>
      )}
    </div>
  );
}

// ─── Shared entry layout ─────────────────────────────────────────────────────

export function DefaultLayout(props: SharedProps) {
  const titleId = useId();
  return (
    <section className="et-lesson-entry" aria-labelledby={titleId}>
      <header className="et-entry-heading">
        <button
          type="button"
          className="et-entry-close"
          onClick={props.handleClose}
          disabled={props.submitting}
        >
          <X size={15} aria-hidden="true" /> Close
        </button>
        <p>You are about to start learning</p>
        <h1 id={titleId}>{props.currentLessonName || "Your lesson"}</h1>
      </header>
      <div className="et-entry-settings">
        <SelectorsBlock {...props} />
      </div>
      <footer className="et-entry-footer">
        <ActionsBlock
          {...props}
          buttonHeightClass="!h-[44px]"
          buttonTextClass="!text-[14px]"
        />
      </footer>
    </section>
  );
}
